import {
    ARTICLE_TYPE,
    InlineRecircTaxonomy,
    OpinionArticleData,
    OPINION_ARTICLE_TYPE,
} from "~types/types";
import { StructuredData } from "~utils/googleJsonLd";

export const DEFAULT_INTERSECTION_OBSERVER_VISIBILITY_THRESHOLD = 0.5;

export type VIDEO_VIEWPORT_ASPECT_RATIO = "16:9" | "2:3" | "1:1";

export enum BRANDS {
    BUSINESSWEEK = "businessweek",
    CITYLAB = "citylab",
    MARKETS = "markets",
    TECHNOLOGY = "technology",
    GREEN = "green",
    OPINION = "opinion",
    VIEW = "view",
    MARKETS_MAGAZINE = "markets-magazine",
    WEEKEND_EDITION = "weekend-edition",
}

export const NEWSLETTER_TOUT_CLASSES: Record<string, string> = {
    top: "inline-newsletter-top",
    middle: "inline-newsletter-middle",
    bottom: "inline-newsletter-bottom",
    leftRail: "left-rail-newsletter",
} as const;

export const AP = "ap";
export const LINKEDIN = "linkedin";
export const PRESS_ASSOCIATION_IDENTIFIER = "THE PRESS ASSOCIATION";
export const PRESS_ASSOCIATION_SHORTHAND = "Press Association";

export const TRANSPORT_COUNT_LIMIT = 3;

export const BRANDS_WITH_PILLARS = new Set([
    "opinion",
    "gadfly",
    "view",
    "green",
]);

// Google uses schema.org's types for SEO purposes (https://developers.google.com/search/docs/appearance/structured-data/intro-structured-data)
// https://schema.org/CollectionPage
export const DEFAULT_SCHEMA_TYPE = "CollectionPage";

export const FRANCHISE_STRUCTURED_DATA: Record<string, StructuredData> = {
    cryptocurrencies: {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Cryptocurrencies",
        url: "https://www.bloomberg.com/cryptocurrencies",
    },
    cybersecurity: {
        name: "Cybersecurity",
        "@type": DEFAULT_SCHEMA_TYPE,
        url: "https://www.bloomberg.com/cybersecurity",
    },
    hyperdrive: {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Electric cars",
        url: "https://www.bloomberg.com/hyperdrive",
    },
    "climate-changed": {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Climate change",
        url: "https://www.bloomberg.com/climate-changed",
    },
    view: {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Opinion",
        url: "https://www.bloomberg.com/opinion",
    },
};

export const BRAND_STRUCTURED_DATA: Record<string, StructuredData> = {
    business: {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Business news",
        url: "https://www.bloomberg.com/",
    },
    markets: {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Markets",
        url: "https://www.bloomberg.com/markets",
    },
    "markets-magazine": {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Markets Magazine",
        url: "https://www.bloomberg.com/markets-magazine",
    },
    technology: {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Technology",
        url: "https://www.bloomberg.com/technology",
    },
    politics: {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Politics",
        url: "https://www.bloomberg.com/politics",
    },
    pursuits: {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Luxury",
        url: "https://www.bloomberg.com/pursuits",
    },
    opinion: {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Opinion",
        url: "https://www.bloomberg.com/opinion",
    },
    businessweek: {
        name: "Bloomberg Businessweek",
        url: "https://www.bloomberg.com/businessweek",
        "@type": DEFAULT_SCHEMA_TYPE,
    },
    gadfly: {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Opinion",
        url: "https://www.bloomberg.com/opinion",
    },
    view: {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Opinion",
        url: "https://www.bloomberg.com/opinion",
    },
};

export const LABEL_STRUCTURED_DATA: Record<string, StructuredData> = {
    Business: {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Opinions on Business",
        url: "https://www.bloomberg.com/opinion-business",
    },
    Finance: {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Opinions on Finance",
        url: "https://www.bloomberg.com/opinion-finance",
    },
    Economics: {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Opinions on Economics",
        url: "https://www.bloomberg.com/opinion-economics",
    },
    Markets: {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Opinions on Markets",
        url: "https://www.bloomberg.com/opinion-markets",
    },
    "Politics & Policy": {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Opinions on Politics & Policy",
        url: "https://www.bloomberg.com/opinion-politics-and-policy",
    },
    "Technology & Ideas": {
        "@type": DEFAULT_SCHEMA_TYPE,
        name: "Opinions on Technology & Ideas",
        url: "https://www.bloomberg.com/opinion-technology-and-ideas",
    },
};

export const INLINE_RECIRC_TAXONOMIES: Record<string, InlineRecircTaxonomy> = {
    pursuits: {
        name: "Pursuits",
        url: "/pursuits",
        enabled: true,
        cobrandEnabled: true,
    },
    green: {
        name: "Green",
        url: "/green",
        enabled: true,
        cobrandEnabled: true,
    },
    citylab: {
        name: "CityLab",
        url: "/citylab",
        enabled: true,
        cobrandEnabled: true,
    },
    equality: {
        name: "Equality",
        url: "/equality",
        enabled: true,
        cobrandEnabled: true,
    },
    "new-economy-forum": {
        name: "New Economy Forum",
        site: "new_economy_forum",
        url: "/new-economy-forum",
        enabled: true,
        cobrandEnabled: false,
    },
    hyperdrive: {
        name: "Hyperdrive",
        url: "/hyperdrive",
        enabled: true,
        cobrandEnabled: false,
    },
    prognosis: {
        name: "Prognosis",
        url: "/prognosis",
        enabled: true,
        cobrandEnabled: false,
    },
    screentime: {
        name: "Screentime",
        url: "/screentime",
        enabled: true,
        cobrandEnabled: false,
    },
    view: {
        name: "Opinion",
        url: "/opinion",
        enabled: false,
        cobrandEnabled: false,
    },
    opinion: {
        name: "Opinion",
        url: "/opinion",
        enabled: false,
        cobrandEnabled: false,
    },
    business: {
        name: "Business",
        url: "/",
        enabled: false,
        cobrandEnabled: true,
    },
    markets: {
        name: "Markets",
        url: "/markets",
        enabled: false,
        cobrandEnabled: true,
    },
    technology: {
        name: "Technology",
        url: "/technology",
        enabled: false,
        cobrandEnabled: true,
    },
    politics: {
        name: "Politics",
        url: "/poltics",
        enabled: false,
        cobrandEnabled: true,
    },
    wealth: {
        name: "Wealth",
        url: "/wealth",
        enabled: false,
        cobrandEnabled: true,
    },
    businessweek: {
        name: "Businessweek",
        url: "/businessweek",
        enabled: true,
        cobrandEnabled: true,
    },
};

export const WEEKEND_NICODES: Record<string, string> = {
    all: "WKNDEDIT",
    curated: "WKNDCUR",
};

export const TABLE_CELL_CLASS_TYPES: Record<string, string> = {
    "news-rsf-table-number": "number",
    "news-rsf-table-string": "text",
};

export const ARTICLE_DATA_PAGE_TYPE: Record<ARTICLE_TYPE, string> = {
    article: "basic_article",
    feature: "feature_article",
    slideshow: "slideshow_article",
    newsletter_campaign: "basic_article",
    letter: "letter",
    quicktake: "quicktake",
    breaking_news: "breaking_news",
    explainers: "explainers",
};

export const ARTICLE_TYPES: {
    default: ARTICLE_TYPE;
    [key: string]: ARTICLE_TYPE;
} = {
    default: ARTICLE_TYPE.BASIC,
    article: ARTICLE_TYPE.BASIC,
    feature: ARTICLE_TYPE.FEATURE,
    slideshow: ARTICLE_TYPE.SLIDESHOW,
    newsletter: ARTICLE_TYPE.NEWSLETTER,
    explainers: ARTICLE_TYPE.EXPLAINERS,
};

export const OPINION_ARTICLE_TYPES: Record<string, OpinionArticleData> = {
    [OPINION_ARTICLE_TYPE.MRB]: {
        isInlineRecircEnabled: false,
    },
    [OPINION_ARTICLE_TYPE.EDITORIAL_BOARD]: {
        isInlineRecircEnabled: false,
    },
    [OPINION_ARTICLE_TYPE.GUEST_ESSAY]: {
        isInlineRecircEnabled: false,
    },
    [OPINION_ARTICLE_TYPE.COLUMNIST]: {
        isInlineRecircEnabled: true,
    },
};

export const TERMINAL_LANGUAGES: Record<string, string> = {
    DEFAULT: "en",
    ENGLISH: "en",
    JAPANESE: "ja",
    GERMAN: "de",
    SPANISH: "es",
    PORTUGUESE: "pt",
};

export const HTML_ENTITIES: Record<string, string> = {
    nbsp: " ",
    amp: "&",
    quot: '"',
    lt: "<",
    gt: ">",
};

export const BLENS_ARTICLE_TYPES: Partial<Record<ARTICLE_TYPE, boolean>> = {
    article: true,
    feature: true,
};

export const ONE_DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
